<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import { IonCard } from '@ionic/vue';

import BaseButton from '@/components/BaseButton.vue';
import { clickContentWithLink } from '@/helper/content/links/link-handler';
import { hasValidLink } from '@/helper/content/links/link-validation';
import type { StoryItem } from '@/types/stories';

interface Props {
  item: StoryItem;
  index: number;
  isActive: boolean;
  progressBarRef: (el: Element | ComponentPublicInstance | null) => void;
}

defineProps<Props>();

const emit = defineEmits<{
  pauseStart: [event: MouseEvent | TouchEvent];
  pauseEnd: [event: MouseEvent | TouchEvent];
}>();

const handlePauseStart = (event: MouseEvent | TouchEvent) => {
  if ((event.target as HTMLElement).closest('.slider-button, base-button')) {
    return;
  }
  event.preventDefault();
  emit('pauseStart', event);
};

const handlePauseEnd = (event: MouseEvent | TouchEvent) => {
  if ((event.target as HTMLElement).closest('.slider-button, base-button')) {
    return;
  }
  event.preventDefault();
  emit('pauseEnd', event);
};
</script>

<template>
  <ion-card
    class="slider-card"
    :class="{ 'slider-card--active': isActive }"
    @mousedown.stop="handlePauseStart($event)"
    @mouseup.stop="handlePauseEnd($event)"
    @mouseleave="handlePauseEnd($event)"
    @touchstart.stop="handlePauseStart($event)"
    @touchend.stop="handlePauseEnd($event)"
    @touchcancel="handlePauseEnd($event)"
  >
    <div class="progress-container">
      <div
        v-if="isActive"
        :ref="progressBarRef"
        class="progress-bar"
      />
    </div>
    <div>
      <div 
        v-if="item.image" 
        class="slider-image-wrapper"
      >
        <img 
          class="slider-image"
          :src="item.image"
        />
      </div>
      <h2>{{ item.headline }}</h2>
      <p
        v-external-links
        v-html="item.bodytext"
      ></p>
    </div>
    <div
      v-if="hasValidLink(item)"
      class="slider-button"
    >
      <base-button
        :text="item.cta_text"
        variant="tertiary"
        next
        @click.prevent="clickContentWithLink(item, 'Stories')"
      >
        {{ item.cta_text }}
      </base-button>
    </div>
  </ion-card>
</template>

<style lang="less" scoped>
.progress-container {
  width: 100%;
  background-color: var(--primary-100) !important;
  height: 6px;
  border-radius: 10px !important;
  margin-bottom: var(--gap-xs);
}

.progress-bar {
  height: 6px;
  width: 0%;
  background-color: var(--orange-900);
  border-radius: 10px;
}

.slider-card {
  padding: 0;
  margin: 0;
  height: calc(
    100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 60px -
      40px - var(--base-gap) - 14px
  );
  color: var(--primary-900);
  display: flex;
  flex-direction: column;
  overflow-y: scroll;

  p {
    font-size: var(--base-font-size);
    line-height: var(--base-line-height);
    font-weight: 400;
  }

  .slider-button {
    margin-top: auto;
  }

  > div:not(.progress-container):not(.slider-button) {
    width: 100%;
    min-width: 0;
  }
}

.slider-image-wrapper {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: var(--base-border-radius);
  aspect-ratio: 16 / 9;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
}

.slider-image {
  display: block;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  border-radius: var(--base-border-radius);
  image-rendering: auto;
}

h2 {
  color: var(--primary-900);
  font-weight: 600;
}
</style>
